<template>
  <div v-if="show" class="panel">
    <div class="rowTop">
      <div class="title">Agent run in progress</div>
      <div class="muted" style="font-size:12px;">{{ progressLabel }}</div>
    </div>
    <div class="progressBar" aria-hidden="true">
      <div class="progressFill" :style="{ width: `${pct}%` }"></div>
    </div>
    <div class="muted" style="font-size:12px; margin-top:8px;">{{ budgetLabel }}</div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

const props = defineProps({
  status: { type: String, default: '' },
  startedAt: { type: String, default: '' },
  maxRuntimeSeconds: { type: Number, default: 0 },
  toolCallsUsed: { type: Number, default: 0 },
  maxToolCalls: { type: Number, default: 0 },
  leadiqQueryCount: { type: Number, default: 0 },
  maxLeadiqQueries: { type: Number, default: 0 },
})

const nowMs = ref(Date.now())
let timer = null

function normalizeStatus(v) {
  return String(v || '').trim().toLowerCase()
}

const show = computed(() => {
  const st = normalizeStatus(props.status)
  return st === 'queued' || st === 'running' || st === 'in_progress' || st === 'in-progress'
})

const runtimeSeconds = computed(() => {
  if (!props.startedAt) return null
  try {
    const t0 = new Date(props.startedAt).getTime()
    if (Number.isNaN(t0)) return null
    return Math.max(0, Math.floor((nowMs.value - t0) / 1000))
  } catch {
    return null
  }
})

const pct = computed(() => {
  const rt = runtimeSeconds.value
  const rtMax = Number(props.maxRuntimeSeconds || 0) || 0
  const tools = Number(props.toolCallsUsed || 0) || 0
  const toolsMax = Number(props.maxToolCalls || 0) || 0
  const li = Number(props.leadiqQueryCount || 0) || 0
  const liMax = Number(props.maxLeadiqQueries || 0) || 0

  const rtPct = rt !== null && rtMax > 0 ? (rt / rtMax) * 100 : 0
  const toolsPct = toolsMax > 0 ? (tools / toolsMax) * 100 : 0
  const liPct = liMax > 0 ? (li / liMax) * 100 : 0

  return Math.min(100, Math.max(0, Math.round(Math.max(rtPct, toolsPct, liPct))))
})

const progressLabel = computed(() => {
  const rt = runtimeSeconds.value
  if (rt === null) return 'working…'
  return `${rt}s elapsed`
})

const budgetLabel = computed(() => {
  const rt = runtimeSeconds.value
  const rtMax = Number(props.maxRuntimeSeconds || 0) || 0
  const tools = Number(props.toolCallsUsed || 0) || 0
  const toolsMax = Number(props.maxToolCalls || 0) || 0
  const li = Number(props.leadiqQueryCount || 0) || 0
  const liMax = Number(props.maxLeadiqQueries || 0) || 0

  const parts = []
  if (rt !== null && rtMax > 0) parts.push(`Runtime ${rt}/${rtMax}s`)
  if (toolsMax > 0) parts.push(`Tools ${tools}/${toolsMax}`)
  if (liMax > 0) parts.push(`LeadIQ ${li}/${liMax}`)
  return parts.length ? parts.join(' · ') : 'Working…'
})

onMounted(() => {
  timer = setInterval(() => {
    nowMs.value = Date.now()
  }, 1000)
})

onBeforeUnmount(() => {
  if (timer) {
    clearInterval(timer)
    timer = null
  }
})
</script>

<style scoped>
.panel {
  padding: 12px;
}

.rowTop {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.title {
  font-weight: 700;
}

.muted {
  color: var(--muted);
}

.progressBar {
  margin-top: 8px;
  height: 10px;
  width: 100%;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: #f3f4f6;
  overflow: hidden;
}

.progressFill {
  height: 100%;
  background: linear-gradient(90deg, var(--accent), #22c55e);
  border-radius: 999px;
  transition: width 200ms ease;
}
</style>
